import { findAccountingIntegration, type IntegrationWithJobInfo } from "../../../api/integration"

/** The accounting systems whose invoice lines the actualizer reads. Xero and Sage sync no lines. */
export type ActualsProvider = "quickbooks" | "netsuite"

export interface ActualsSyncButton {
  /** Shown at all: the org has a QuickBooks or NetSuite connection. */
  visible: boolean
  label: string
  provider: ActualsProvider | null
  /** Display name of the provider, for toasts ("QuickBooks" / "NetSuite"). */
  provider_label: string
  /**
   * NetSuite only: when the daily job last pulled invoice lines (ISO), read from the connection's
   * `invoice_lines_synced_through`. `null` when it has not run yet.
   */
  last_pull: string | null
}

const HIDDEN: ActualsSyncButton = { visible: false, label: "", provider: null, provider_label: "", last_pull: null }

/**
 * Which "Sync actuals" button the forecast page shows for this org. QuickBooks and NetSuite are
 * the two systems that give the actualizer invoice lines; any other accounting connection, or none,
 * hides the button rather than offering a sync that writes nothing. While the integrations are
 * still loading the button stays hidden too, so it never flashes the wrong label.
 */
export function actuals_sync_button(integrations: IntegrationWithJobInfo[] | undefined): ActualsSyncButton {
  if (!integrations) return HIDDEN
  const accounting = findAccountingIntegration(integrations)
  switch (accounting?.name) {
    case "quickbooks":
      return { visible: true, label: "Sync actuals from QuickBooks", provider: "quickbooks", provider_label: "QuickBooks", last_pull: null }
    case "netsuite": {
      const raw = accounting.data?.invoice_lines_synced_through
      return {
        visible: true,
        label: "Sync actuals from NetSuite",
        provider: "netsuite",
        provider_label: "NetSuite",
        last_pull: typeof raw === "string" && raw ? raw : null,
      }
    }
    default:
      return HIDDEN
  }
}

/** The sentence the NetSuite sync toast adds about the daily pull. */
export function last_pull_sentence(button: ActualsSyncButton): string | null {
  if (button.provider !== "netsuite") return null
  if (!button.last_pull) return "NetSuite invoice lines have not been pulled yet; the daily sync pulls them."
  return `NetSuite invoice lines last pulled ${new Date(button.last_pull).toLocaleString()}.`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function integration(name: string, data: Record<string, unknown> = {}): IntegrationWithJobInfo {
    return { id: name, name, data } as unknown as IntegrationWithJobInfo
  }

  describe("actuals_sync_button", () => {
    it("labels the button by the accounting system that has lines", () => {
      expect(actuals_sync_button([integration("gmail"), integration("quickbooks")])).toMatchObject({
        visible: true,
        label: "Sync actuals from QuickBooks",
        provider: "quickbooks",
      })
      expect(actuals_sync_button([integration("netsuite", { invoice_lines_synced_through: "2026-10-28T06:00:00Z" })])).toEqual({
        visible: true,
        label: "Sync actuals from NetSuite",
        provider: "netsuite",
        provider_label: "NetSuite",
        last_pull: "2026-10-28T06:00:00Z",
      })
    })

    it("hides the button without QuickBooks or NetSuite, and while loading", () => {
      expect(actuals_sync_button([integration("gmail"), integration("kehe")]).visible).toBe(false)
      expect(actuals_sync_button([integration("xero")]).visible).toBe(false)
      expect(actuals_sync_button([]).visible).toBe(false)
      expect(actuals_sync_button(undefined).visible).toBe(false)
    })

    it("says when NetSuite lines were last pulled, or that they have not been", () => {
      expect(last_pull_sentence(actuals_sync_button([integration("netsuite")]))).toMatch(/not been pulled yet/)
      expect(last_pull_sentence(actuals_sync_button([integration("netsuite", { invoice_lines_synced_through: "2026-10-28T06:00:00Z" })]))).toMatch(
        /last pulled/
      )
      expect(last_pull_sentence(actuals_sync_button([integration("quickbooks")]))).toBeNull()
    })
  })
}
